"use client";

import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { ComboboxOptions } from "@/types/common-type";
import { ScrollArea } from "@radix-ui/react-scroll-area";
import { CommandList } from "cmdk";
import { Check, ChevronsUpDown, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";

export type ComboboxMode = "single" | "multiple";

export interface SelectProps {
  selected: string | string[];
  mode?: ComboboxMode;
  disabled?: boolean;
  initialSelected?: boolean;
  onChange?: (event: string | string[]) => void;
}

interface ComboboxProps {
  mode?: ComboboxMode;
  options: ComboboxOptions[];
  selected?: string | string[];
  className?: string;
  placeholder?: string;
  disabled?: boolean;
  initialSelected?: boolean;
  onChange?: (event: string | string[]) => void;
  // Opt-in remote search: when provided, the input's text is reported here instead of
  // being filtered locally against `options` — the caller is responsible for fetching
  // and passing back the matching options.
  onSearchChange?: (query: string) => void;
}

export function Combobox({
  options,
  selected,
  className,
  placeholder,
  disabled = false,
  initialSelected = false,
  mode = "single",
  onChange,
  onSearchChange,
}: ComboboxProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState(selected || "");

  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const [buttonWidth, setButtonWidth] = useState(0);

  useEffect(() => {
    if (buttonRef.current && buttonRef.current.offsetWidth > 0) {
      setButtonWidth(buttonRef.current.offsetWidth);
    }
  }, []);

  useEffect(() => {
    if (selected) {
      if (Array.isArray(selected)) {
        setQuery(selected.filter((item) => item !== ""));
      } else {
        setQuery(selected);
      }
    } else {
      if (initialSelected && options.length) {
        setQuery(options[0].value);
        onChange && onChange(options[0].value);
      }
    }
  }, [initialSelected, onChange, options, selected]);

  return (
    <div className={cn("block", className)}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger ref={buttonRef} asChild>
          <Button
            disabled={disabled}
            variant="outline"
            role="combobox"
            aria-expanded={open}
            style={{ maxWidth: buttonWidth || "auto" }}
            className="h-auto min-h-10 w-full justify-between px-3 py-2 text-left"
          >
            {query && query.length > 0 ? (
              <div className="relative mr-auto flex min-w-0 flex-1 overflow-hidden pr-2">
                <span className="line-clamp-2 break-words text-left leading-5">
                  {mode === "multiple" && Array.isArray(query)
                    ? query
                        .filter((item) => item)
                        .map(
                          (selectedValue: string) =>
                            options.find((item) => item.value === selectedValue)
                              ?.displayLabel ??
                            options.find((item) => item.value === selectedValue)
                              ?.label
                        )
                        .join(", ")
                    : mode === "single" &&
                      (options.find((item) => item.value === query)
                        ?.displayLabel ??
                        options.find((item) => item.value === query)?.label)}
                </span>
              </div>
            ) : (
              <span className="line-clamp-2 break-words text-left leading-5">
                {placeholder ?? "Select Item..."}
              </span>
            )}
            {query && query.length > 0 ? (
              <div
                onClick={(e) => {
                  e.stopPropagation();
                  if (mode === "multiple") {
                    setQuery([]);
                    onChange && onChange([]);
                  } else {
                    setQuery("");
                    onChange && onChange("");
                  }
                }}
                className="ml-2 flex h-4 w-4 shrink-0 cursor-pointer items-center justify-center rounded-sm opacity-50 transition-opacity hover:opacity-100"
              >
                <X className="h-4 w-4" />
              </div>
            ) : (
              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 self-center opacity-50" />
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="p-0">
          <Command shouldFilter={!onSearchChange}>
            <CommandInput
              placeholder="Search item..."
              onValueChange={onSearchChange}
            />
            <ScrollArea className="max-h-72 overflow-auto">
              <CommandEmpty>No item found.</CommandEmpty>
              <CommandGroup>
                <CommandList>
                  {options.map((option) => (
                    <CommandItem
                      className="flex items-center gap-x-2"
                      key={option.value}
                      value={option.label}
                      onSelect={() => {
                        if (mode === "multiple" && Array.isArray(query)) {
                          if (onChange)
                            onChange(
                              query.includes(option.value)
                                ? query.filter(
                                    (item) => item !== option.value && item
                                  )
                                : [...query, option.value]
                            );
                        } else {
                          if (onChange) {
                            const selectedValue =
                              option.value === query ? "" : option.value;
                            setQuery(selectedValue);
                            onChange(selectedValue);
                          }
                          setOpen(false);
                        }
                      }}
                    >
                      <Check
                        className={cn(
                          `mt-0.5 h-4 w-4 shrink-0 ${
                            mode === "multiple" && Array.isArray(query)
                              ? query.includes(option.value)
                                ? "opacity-100"
                                : "opacity-0"
                              : query === option.value
                                ? "opacity-100"
                                : "opacity-0"
                          }`
                        )}
                      />
                      <div className="flex min-w-0 flex-1 items-start justify-between gap-2">
                        <span className="whitespace-normal break-words leading-5">
                          {option.label}
                        </span>
                        {option.badgeLabel ? (
                          <span
                            className={cn(
                              "shrink-0 rounded-full px-2 py-0.5 text-[11px] font-semibold",
                              option.badgeClassName
                            )}
                          >
                            {option.badgeLabel}
                          </span>
                        ) : null}
                      </div>
                    </CommandItem>
                  ))}
                </CommandList>
              </CommandGroup>
            </ScrollArea>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}
